import React, { useState } from "react";
import {
  FaArrowRight,
  FaExternalLinkAlt,
  FaTimes,
  FaGlobe,
  FaApple,
  FaGooglePlay,
} from "react-icons/fa";
import projectsData from "../projects.json";
import "../Styles/Projects.css";

const LINK_KINDS = {
  web: { icon: FaGlobe, label: "Visit Site" },
  ios: { icon: FaApple, label: "App Store" },
  android: { icon: FaGooglePlay, label: "Google Play" },
};

const Lightbox = ({ images, startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex);

  if (!images || images.length === 0) return null;

  const prev = (e) => {
    e.stopPropagation();
    setIndex((i) => (i - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setIndex((i) => (i + 1) % images.length);
  };

  return (
    <div className="lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="lightbox__close" onClick={onClose} aria-label="Close">
        <FaTimes />
      </button>
      <button className="lightbox__nav lightbox__nav--prev" onClick={prev} aria-label="Previous">
        ‹
      </button>
      <img
        src={images[index]}
        alt=""
        className="lightbox__image"
        onClick={(e) => e.stopPropagation()}
      />
      <button className="lightbox__nav lightbox__nav--next" onClick={next} aria-label="Next">
        ›
      </button>
      <div className="lightbox__counter">
        {index + 1} / {images.length}
      </div>
    </div>
  );
};

const ProjectImageGrid = ({ images, onOpen }) => {
  if (!images || images.length === 0) return null;
  const visible = images.slice(0, 6);
  return (
    <div className="project-images">
      {visible.map((src, i) => (
        <button
          key={src}
          type="button"
          className={`project-image ${i === 0 ? "project-image--feature" : ""}`}
          onClick={() => onOpen(images, i)}
        >
          <img src={src} alt="" loading="lazy" />
        </button>
      ))}
    </div>
  );
};

const Projects = () => {
  const { header, categories } = projectsData;
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = (images, startIndex) => {
    setLightbox({ images, startIndex });
  };

  return (
    <div className="projects-page page-shell">
      <section className="page-hero">
        <div className="page-hero-inner">
          <span className="section-badge">{header.badge}</span>
          <h1 className="page-hero-title">{header.title}</h1>
          <p className="page-hero-subtitle">{header.subtitle}</p>
        </div>
      </section>

      <div className="projects-container">
        {categories.map((category) => (
          <section
            key={category.id}
            id={category.id}
            className="project-category"
          >
            <header className="project-category-header">
              <span className="project-category-eyebrow">
                {category.title}
              </span>
              <p className="project-category-description">
                {category.description}
              </p>
            </header>

            <div className="project-list">
              {category.items.map((item) => {
                const hasGallery = item.images && item.images.length > 0;
                return (
                <article
                  key={item.slug}
                  className={`project-card${hasGallery ? "" : " project-card--solo"}`}
                >
                  <div className="project-card-body">
                    <div className="project-card-header">
                      <h2 className="project-card-name">{item.name}</h2>
                      {item.tagline && (
                        <p className="project-card-tagline">{item.tagline}</p>
                      )}
                    </div>

                    <p className="project-card-summary">{item.summary}</p>

                    {item.highlights && item.highlights.length > 0 && (
                      <ul className="project-card-highlights">
                        {item.highlights.map((highlight, i) => (
                          <li key={i}>
                            <span className="project-card-bullet" />
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    )}

                    {item.stack && item.stack.length > 0 && (
                      <div className="project-card-stack">
                        {item.stack.map((tech) => (
                          <span key={tech} className="project-stack-pill">
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}

                    {item.links && item.links.length > 0 && (
                      <div className="project-card-links">
                        {item.links.map((link) => {
                          const spec = LINK_KINDS[link.kind];
                          const Icon = spec ? spec.icon : FaExternalLinkAlt;
                          const label = link.label || (spec ? spec.label : link.url);
                          return (
                            <a
                              key={link.url}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`project-card-link project-card-link--${link.kind || "external"}`}
                            >
                              <Icon /> {label}
                            </a>
                          );
                        })}
                      </div>
                    )}

                    {item.projects && item.projects.length > 0 && (
                      <div className="project-subprojects">
                        {item.projects.map((sub) => (
                          <div key={sub.name} className="project-subproject">
                            <div className="project-subproject-head">
                              <h3>{sub.name}</h3>
                              {sub.url && (
                                <a
                                  href={sub.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="project-subproject-link"
                                  aria-label={`Visit ${sub.name}`}
                                >
                                  Visit <FaArrowRight />
                                </a>
                              )}
                            </div>
                            <p>{sub.description}</p>
                            {sub.images && sub.images.length > 0 && (
                              <ProjectImageGrid
                                images={sub.images}
                                onOpen={openLightbox}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {hasGallery && (
                    <div className="project-card-gallery">
                      <ProjectImageGrid
                        images={item.images}
                        onOpen={openLightbox}
                      />
                    </div>
                  )}
                </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.startIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

export default Projects;
